import {useEffect} from 'react'
import {useFormikContext} from 'formik'
import {calculateDaysDifferenceWithTime} from '../../utils/DateUtil'
import {IReservationModel} from './ReservationModel'
import {getTotalPrice} from '../definition/xml-tariff/Api' 
import { showErrorToast } from '../../../shared/components/ToastUtils'

interface CalculateRentalPriceProps {
  setRemainingAmount: React.Dispatch<React.SetStateAction<number>>
  addinionalServiceAmount: number
  isManualPrice: boolean
}

const CalculateRentalPrice = ({setRemainingAmount, addinionalServiceAmount, isManualPrice}: CalculateRentalPriceProps) => {
  const {values, setFieldValue} = useFormikContext<IReservationModel>()

  /* ------------------------------------------------ */
  /* 1️⃣ RENTAL DAY HESAPLAMA (SADECE TARİH DEĞİŞİNCE) */
  /* ------------------------------------------------ */
  useEffect(() => {
    const {startDate, startTime, endDate, endTime, hourDifferenceTaken} = values

    if (!startDate || !endDate) return

    const diffDays = calculateDaysDifferenceWithTime(
      startDate,
      endDate,
      startTime,
      endTime,
      hourDifferenceTaken
    )

    const fixedDay = Math.max(1, Number(diffDays.toFixed(0)))

    setFieldValue('rentalDay', fixedDay)
    setFieldValue('rentDayAdd', fixedDay)
    setFieldValue('rentalDayDetail', fixedDay)
    setFieldValue('rentalDayReturn', fixedDay)
  }, [
    values.startDate,
    values.startTime,
    values.endDate,
    values.endTime,
    values.hourDifferenceTaken,
    setFieldValue,
    values
  ])

  /* ------------------------------------------------ */
  /* 2️⃣ API'DEN PRICE ÇEKME (SADECE RESERVATION CHANGE) */
  /* ------------------------------------------------ */
  useEffect(() => {
    const reservationSourceId = values.reservationSource?.id
    const rentalDay = Number(values.rentalDay) || 1

    if (!reservationSourceId || !rentalDay || isManualPrice) return
    
    let cancelled = false

    const fetchPrice = async () => {
      try {
        const response = await getTotalPrice({
          reservationSourceId,
          startDate: values.startDate!,
          endDate: values.endDate!,
          branchId: values.pickupBranchId,
          vehicleGroupId: values.vehicleGroupId,
          currencyId: values.currency
        })
 
        if (response.data.hasError) {
          showErrorToast(response.data.friendlyMessage?.description || 'Fiyat hesaplanırken bir hata oluştu.')
          return
        } 
        const price = response.data ?? 0 
        if (!cancelled) {
          setFieldValue('price', price)
        }
      } catch (err: any) {  
        const errorMessage = err?.response?.data?.friendlyMessage?.description
          || 'Fiyat hesaplanırken bir hata oluştu. Lütfen bilgileri kontrol edin ve tekrar deneyin.'
        showErrorToast(errorMessage)
      }
    }

    fetchPrice()

    return () => {
      cancelled = true
    }
  }, [values.reservationSource?.id, values.rentalDay, isManualPrice, setFieldValue, values])

  /* ------------------------------------------------ */
  /* 3️⃣ FİYAT HESAPLAMA (PRICE VE DİĞERLERİ DEĞİŞİNCE) */
  /* ------------------------------------------------ */
  useEffect(() => {
    const {price, discount, rentalDay, additionalServiceAmount = 0} = values

    if (!price || !rentalDay) return

    const numericPrice = Number(price)
    const numericDiscount = Number(discount || 0) 

    const priceAfterDiscount =
      numericDiscount > 0 ? numericPrice * (1 - numericDiscount / 100) : numericPrice

    const totalPrice = priceAfterDiscount
    const rentKdv = totalPrice * 0.2
    const rentAmount = totalPrice - rentKdv

    const totalTl = totalPrice + Number(additionalServiceAmount)
    const totalKdv = additionalServiceAmount ? totalTl * 0.2 : 0
    const kdvTl = totalKdv || rentKdv

    setFieldValue('price1', price)
    setFieldValue('priceAdd', price)
    setFieldValue('priceReturn', price)
    setFieldValue('discount1', discount)
    setFieldValue('totalAmount', totalTl.toFixed(2))
    setFieldValue('subTotalAmount', totalTl.toFixed(2))
    setFieldValue('totalAmount1', totalTl.toFixed(2))
    setFieldValue('totalAmountAdd', totalTl.toFixed(2))
    setFieldValue('totalAmountReturn', totalTl.toFixed(2))
    setFieldValue('rentalAmount', rentAmount.toFixed(2))
    setFieldValue('rentAmount1', rentAmount.toFixed(2))
    setFieldValue('taxAmount', kdvTl.toFixed(2))
    setFieldValue('kdvTl1', kdvTl.toFixed(2))
    setFieldValue('generalTotalWithCurrency', totalTl.toFixed(2))
    setFieldValue('generalTotalWithCurrencyAdd', totalTl.toFixed(2))
    setFieldValue('generalTotalWithCurrencyReturn', totalTl.toFixed(2))
    setFieldValue('additionalServiceAmount', addinionalServiceAmount.toFixed(2))
    setFieldValue('serviceAmount1', addinionalServiceAmount.toFixed(2))
    setFieldValue('grandTotal', totalTl.toFixed(2))
    setFieldValue('priceAfterDiscount', priceAfterDiscount.toFixed(2))
    setFieldValue('priceAfterDiscount1', priceAfterDiscount.toFixed(2))
    setFieldValue('remainingAmount', totalTl.toFixed(2))
    setFieldValue('remaining1', totalTl.toFixed(2))
    setFieldValue('remainingAdd', totalTl.toFixed(2))
    setFieldValue('remainingReturn', totalTl.toFixed(2))
    setRemainingAmount(totalTl)
  }, [values.price, values.discount, values.rentalDay, addinionalServiceAmount, setRemainingAmount, setFieldValue, values])
 
  return null // UI yok, sadece Formik'i izliyor
}

export default CalculateRentalPrice
